// Phase 19 Plan 02 Task 1 — net wrapper for GET /api/replay/{matchId} (META-03).
//
// Plan 19-01 (Wave 1, on main) ships the backend handler:
//   - 200 + Content-Type application/json (D-019-T2-A — NOT a 302 redirect to
//     a presigned URL; presigning would require operator-pending R2 S3-compat
//     secrets that violate DEC-015).
//   - 401 (UNAUTHORIZED), 403 (FORBIDDEN — non-participant), 404 (NOT_FOUND),
//     410 (GONE — in-progress OR cleaned up; D-019-T2-B unified).
//
// ARCA-129: the handler now gunzips the R2 object in the Worker
// (DecompressionStream) and returns plain JSON with NO Content-Encoding header.
// The original design returned the gzipped bytes with `Content-Encoding: gzip`,
// relying on transparent runtime decompression that does NOT happen under
// `wrangler dev` — there the client `response.json()`'d gzip magic bytes and
// threw, surfacing the replay screen's generic error. Either way the client is
// unchanged: `apiFetch<ReplayResponse>(...)` calls `response.json()` and yields
// the parsed ReplayJson directly — no DecompressionStream call on the client.
//
// ReplayJson type is declared LOCALLY here (not imported from
// @runegambit/api-types) because Plan 19-01's spec edits to openapi.yaml are
// owned by Plan 19-04 (Wave 3). Once Plan 19-04 lands the regenerated
// codegen, swap `type ReplayResponse = ReplayJson` (local) for
// `components['schemas']['ReplayResponse']` (codegen) without breaking any
// consumer of this module — the shape is structurally identical.

import { apiFetch } from './client'

/**
 * Phase A discriminated union — mirrors apps/web/src/stores/turnBuilder.ts
 * PhaseASelection AND apps/backend/src/match/replay/serializer.ts.
 *
 * Plan 24-03: replay-side tolerates legacy {kind:'swap'} payloads from
 * terminal pre-Phase-24 matches. The submit-side type (regenerated from
 * openapi.yaml) no longer includes 'swap' — only the replay reader retains
 * it for read-only deserialization of historical match data (RESEARCH §A1).
 */
export type PhaseABody =
  | {
      readonly kind: 'word'
      readonly word: string
      readonly path: ReadonlyArray<number>
    }
  // Plan 24-03: replay-side tolerates legacy {kind:'swap'} payloads from terminal pre-Phase-24 matches. The submit-side type no longer includes 'swap'.
  | { readonly kind: 'swap'; readonly cell_idx: number }
  | { readonly kind: 'skip' }

/**
 * Phase B discriminated union — mirrors the in-game canvas action vocabulary.
 * `extend` is the Phase 13 PWR-05 role-extension action.
 */
export type PhaseBBody =
  | { readonly kind: 'move'; readonly from: number; readonly to: number }
  | {
      readonly kind: 'empower'
      readonly cell_idx: number
      readonly role: string
      readonly source_word_length: number
    }
  | {
      readonly kind: 'powerup'
      readonly name: string
      readonly payload: Record<string, unknown>
    }
  | { readonly kind: 'pass' }
  | { readonly kind: 'extend'; readonly cell_idx: number }

export interface ReplayEconomy {
  readonly bank_a: number
  readonly bank_b: number
  readonly captures_a: number
  readonly captures_b: number
}

export interface ReplayTurnRow {
  readonly turn_idx: number
  readonly player_slot: 0 | 1
  readonly phase_a: PhaseABody
  readonly phase_b: PhaseBBody
  readonly economy: ReplayEconomy
  readonly ts: number
}

export interface BoardCellSnapshot {
  readonly cell_idx: number
  readonly piece: string | null
  readonly owner: 0 | 1 | null
  readonly empowered_role?: string | null
}

/**
 * GET /api/replay/{matchId} response body — the gzipped (wire) /
 * decompressed (in-memory) Phase 4 ReplayJson shape.
 */
export interface ReplayResponse {
  readonly schema_version: 1
  readonly match_id: string
  readonly config: {
    readonly language: 'en' | 'sv'
    readonly length: number
    readonly min_word_length: number
    readonly powerup_loadout: ReadonlyArray<string>
    readonly dictionary_version: string
  }
  readonly deck_seed: string
  readonly turns: ReadonlyArray<ReplayTurnRow>
  readonly final_board: ReadonlyArray<BoardCellSnapshot>
  readonly outcome: {
    readonly status: string
    readonly winner_slot: 0 | 1 | null
    readonly end_reason: string | null
  }
  readonly completed_at: string
}

/**
 * GET /api/replay/{matchId} — fetch the full replay JSON for a terminal
 * match the caller participated in.
 *
 * Errors surface verbatim as `ApiError(status, body)` so the consumer
 * (Task 2's ReplayScreen) can map 401/403/404/410 to user-visible copy
 * via the i18n `replay.error.*` keys.
 *
 * No retry, no special 302-handling — the handler returns the payload
 * inline at 200 (D-019-T2-A); apiFetch's default 5xx single-retry covers
 * transient Worker failures.
 */
export async function fetchReplay(matchId: string): Promise<ReplayResponse> {
  return apiFetch<ReplayResponse>(`/api/replay/${matchId}`, 'GET')
}
